import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { setAuthErrorHandler, authAPI } from '../services/api'
import { clearTtsProfileClientCache } from '../services/ttsProfileClient'

const AuthContext = createContext(null)

const resolveRole = (userData) => {
  const identity = `${userData?.name || ''} ${userData?.email || ''}`.toLowerCase()
  const isTim = identity.includes('tim')
  const role = userData?.role
  if (role === 'admin') return 'admin'
  if (role === 'member') return 'member'
  if (role === 'user') return isTim ? 'admin' : 'member'
  return isTim ? 'admin' : 'member'
}

// Token validation - our tokens are random hex strings, not JWTs
// Tokens are validated server-side; we just check if token exists
function hasValidToken(token) {
  return !!token && typeof token === 'string' && token.length > 0
}

function getDetectedTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [emailVerified, setEmailVerified] = useState(true)

  // Handle auth errors (called from API interceptor)
  const handleAuthError = useCallback(() => {
    localStorage.removeItem('rithmo_user')
    localStorage.removeItem('rithmo_token')
    clearTtsProfileClientCache()
    setUser(null)
    setIsAuthenticated(false)
    setEmailVerified(true)
  }, [])

  useEffect(() => {
    // Check for existing session on mount
    const savedUser = localStorage.getItem('rithmo_user')
    const token = localStorage.getItem('rithmo_token')

    if (savedUser && hasValidToken(token)) {
      try {
        const userData = JSON.parse(savedUser)
        const normalizedUser = { ...userData, role: resolveRole(userData) }
        setUser(normalizedUser)
        setIsAuthenticated(true)
        setEmailVerified(userData.emailVerified !== false)
        if (normalizedUser.role !== userData.role) {
          localStorage.setItem('rithmo_user', JSON.stringify(normalizedUser))
        }
      } catch (e) {
        localStorage.removeItem('rithmo_user')
        localStorage.removeItem('rithmo_token')
      }
    }
    setLoading(false)
  }, [])

  // Register auth error handler with API service
  useEffect(() => {
    setAuthErrorHandler(handleAuthError)
    return () => setAuthErrorHandler(null)
  }, [handleAuthError])

  const login = async (email, password) => {
    if (!email || !password) {
      throw new Error('Email and password are required')
    }
    try {
      const detectedTz = getDetectedTimezone()
      const res = await authAPI.login(email, password, detectedTz || null)
      const payload = res?.data ?? res
      const token = payload?.token ?? res?.token
      const userData = payload?.user ?? res?.user
      const verified = payload?.email_verified ?? res?.email_verified ?? true
      const setupProfile = payload?.setupProfile ?? res?.setupProfile
      const nextSetupPath = payload?.nextSetupPath ?? res?.nextSetupPath
      if (token) {
        localStorage.setItem('rithmo_token', token)
      }
      const userBase = userData
        ? { ...userData, emailVerified: verified, role: resolveRole(userData) }
        : {
            id: 'user_' + Date.now(),
            email,
            name: email.split('@')[0],
            avatar: null,
            emailVerified: verified,
            role: `${email || ''}`.toLowerCase().includes('tim') ? 'admin' : 'member',
            createdAt: new Date().toISOString(),
            settings: { notifications: true, theme: 'dark', defaultMeetingDuration: 30 },
            integrations: { google: false, outlook: false, zoom: false },
          }
      const user = setupProfile !== undefined ? { ...userBase, setupProfile } : userBase
      const userWithPaths =
        typeof nextSetupPath === 'string' && nextSetupPath.trim()
          ? { ...user, nextSetupPath }
          : user
      localStorage.setItem('rithmo_user', JSON.stringify(userWithPaths))
      setUser(userWithPaths)
      setIsAuthenticated(true)
      setEmailVerified(verified)
      clearTtsProfileClientCache()
      return userWithPaths
    } catch (err) {
      throw new Error(err.message || 'Invalid email or password')
    }
  }

  const signup = async (email, password, name, metadata = {}) => {
    const company_name = typeof metadata.company_name === 'string' ? metadata.company_name.trim() : ''
    const job_title = typeof metadata.job_title === 'string' ? metadata.job_title.trim() : ''
    const signup_role_band = metadata.signup_role_band
    if (!email || !password || !(name ?? '').trim() || !company_name || !job_title || !signup_role_band) {
      throw new Error('Please fill in all fields')
    }
    try {
      const timezone = getDetectedTimezone()
      const body = {
        full_name: name,
        email,
        password,
        timezone: timezone || undefined,
        company_name,
        job_title,
        signup_role_band,
      }
      const res = await authAPI.signup(body)
      const payload = res?.data ?? res
      const token = payload?.token ?? res?.token
      const userData = payload?.user ?? res?.user
      const verified = payload?.email_verified ?? res?.email_verified ?? true
      const setupProfile = payload?.setupProfile ?? res?.setupProfile
      const nextSetupPath = payload?.nextSetupPath ?? res?.nextSetupPath
      if (token) {
        localStorage.setItem('rithmo_token', token)
      }
      const userBase = userData
        ? { ...userData, emailVerified: verified, role: resolveRole(userData) }
        : {
            id: 'user_' + Date.now(),
            email,
            name,
            avatar: null,
            emailVerified: verified,
            role: `${name || ''} ${email || ''}`.toLowerCase().includes('tim') ? 'admin' : 'member',
            createdAt: new Date().toISOString(),
            settings: { notifications: true, theme: 'dark', defaultMeetingDuration: 30 },
            integrations: { google: false, outlook: false, zoom: false },
          }
      const withProfile = setupProfile !== undefined ? { ...userBase, setupProfile } : userBase
      const userStored =
        typeof nextSetupPath === 'string' && nextSetupPath.trim()
          ? { ...withProfile, nextSetupPath: nextSetupPath.trim() }
          : withProfile
      localStorage.setItem('rithmo_user', JSON.stringify(userStored))
      setUser(userStored)
      setIsAuthenticated(true)
      setEmailVerified(verified)
      clearTtsProfileClientCache()
      return userStored
    } catch (err) {
      throw new Error(err.message || 'Please fill in all fields')
    }
  }

  const logout = async () => {
    try {
      const token = localStorage.getItem('rithmo_token')
      if (token) {
        await fetch(`${import.meta.env.VITE_API_URL || '/api'}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {})
      }
    } finally {
      localStorage.removeItem('rithmo_user')
      localStorage.removeItem('rithmo_token')
      clearTtsProfileClientCache()
      setUser(null)
      setIsAuthenticated(false)
      setEmailVerified(true)
    }
  }

  const updateUser = (updates) => {
    const normalizedUpdates = { ...updates }
    if (normalizedUpdates.role === 'user') {
      normalizedUpdates.role = 'member'
    }
    const updatedUser = {
      ...user,
      ...normalizedUpdates,
      role: normalizedUpdates.role || resolveRole({ ...user, ...normalizedUpdates }),
    }
    localStorage.setItem('rithmo_user', JSON.stringify(updatedUser))
    setUser(updatedUser)
  }

  const verifyEmail = async (otp) => {
    const res = await authAPI.verifyEmail(otp)
    if (res?.data?.verified || res?.verified) {
      setEmailVerified(true)
      const updated = { ...user, emailVerified: true }
      setUser(updated)
      localStorage.setItem('rithmo_user', JSON.stringify(updated))
    }
    return res
  }

  const resendOtp = async () => {
    return authAPI.resendOtp()
  }

  const value = {
    user,
    loading,
    isAuthenticated,
    emailVerified,
    login,
    signup,
    logout,
    updateUser,
    verifyEmail,
    resendOtp,
    handleAuthError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
